import React, { useState, useMemo, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, palette } from '@resiliatech/birdsong'
import { Box } from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { InfoOutlined } from '@material-ui/icons'
import { Goal, GoalUpdateInput, Campaign } from 'Generated/graphql-types'
import { useMutation } from '@apollo/client'
import UPDATE_GOAL from 'Apollo/Mutations/UpdateGoal'
import { GOALS } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import { FormattedCampaignGoalsData } from 'TypeDefs/Goal'
import {
  Actions,
  Chart,
  Header,
  HeaderContainer,
  ProgressChartContainer,
  Selectors,
  DisconnectedCampaign,
  DisconnectedCampaignHeader,
  DisconnectedCampaignBody,
  CalculationType,
  StyledAnchor,
} from './CampaignProgressChart.styled'
import { ChartTypeSelector } from '../ChartTypeSelector/ChartTypeSelector'
import { EditChartModal } from '../EditChartModal/EditChartModal'
import { ChartTypeOptionsEnum, ChartCalculationOptionsEnum } from '../types'
import { GoalChart } from '../GoalChart/GoalChart'
import { getSeriesDataFromCampaignData } from '../utils'
import { ChartCalculationSelector } from '../ChartCalculationSelector/ChartCalculationSelector'
import { Spacer } from '../ProgressChart/ProgressChart.styled'

type CampaignProgressChartProps = {
  goal: Goal
}

const iconStyles = {
  marginRight: '10px',
}

export const CampaignProgressChart = ({ goal }: CampaignProgressChartProps) => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId },
    donations: {
      campaigns: { isLoading: isCampaignsLoading, data: campaignsData },
    },
  } = useAppState()
  const history = useHistory()
  const [isEditingChart, setIsEditingChart] = useState(false)
  const [chartType, setChartType] = useState<ChartTypeOptionsEnum>(
    ChartTypeOptionsEnum.line
  )

  const [chartCalculation, setChartCalculation] =
    useState<ChartCalculationOptionsEnum>(
      ChartCalculationOptionsEnum.cumulative
    )
  const [campaignGoals, setCampaignGoals] =
    useState<FormattedCampaignGoalsData>([])

  // Group campaigns by goalId
  const formattedCampaignGoalsData = useMemo(() => {
    if (isCampaignsLoading || !campaignsData) return null

    const campaignsByGoalId: Record<number, Campaign[]> = {}

    campaignsData.forEach((campaign) => {
      const goalId = campaign.initiativeGoalId

      if (goalId != null) {
        // If an array of campaigns already exists for the current goalId, append the current campaign.
        // If not, create a new array with the current campaign
        campaignsByGoalId[goalId] = campaignsByGoalId[goalId]
          ? [...campaignsByGoalId[goalId], campaign]
          : [campaign]
      }
    })

    return campaignsByGoalId
  }, [campaignsData, isCampaignsLoading])

  useEffect(() => {
    if (!isCampaignsLoading && formattedCampaignGoalsData) {
      setCampaignGoals(formattedCampaignGoalsData)
    }
  }, [formattedCampaignGoalsData, isCampaignsLoading])

  const [updateGoal] = useMutation(UPDATE_GOAL, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
  })

  const handleEditChart = async (goalUpdateInfo: GoalUpdateInput) => {
    await updateGoal({ variables: { goalUpdateInfo } })
    setIsEditingChart(false)
  }

  const routeToCampaigns = () => {
    history.push(`/fundraising/donations/campaigns`)
  }

  const { data: chartData } = getSeriesDataFromCampaignData({
    goal,
    goalCampaigns: campaignGoals[goal.id],
    chartCalculation,
  })
  const { seriesData } = chartData.chart
  const hasSeriesData = seriesData.length > 0
  const enablePieChartOption = seriesData.length > 1 || false

  return (
    <ProgressChartContainer>
      <HeaderContainer>
        <Header>Progress Chart</Header>
        <Actions>
          {hasSeriesData && (
            <Button
              variant='tertiary'
              label='Edit Chart'
              onClick={() => setIsEditingChart(true)}
            />
          )}
        </Actions>
      </HeaderContainer>
      {hasSeriesData && (
        <Chart>
          <Selectors>
            <ChartTypeSelector
              chartType={chartType}
              onChange={setChartType}
              enablePieChartOption={enablePieChartOption}
            />
            <Spacer />
            {chartType !== 'Pie' && (
              <ChartCalculationSelector
                calculationType={chartCalculation}
                onChange={setChartCalculation}
              />
            )}
          </Selectors>
          <GoalChart
            chartType={chartType}
            goal={goal}
            pieChartSeriesData={seriesData}
            series={seriesData}
            campaignTarget={chartData.chart.target}
          />
        </Chart>
      )}
      {hasSeriesData && !isCampaignsLoading && (
        <CalculationType>
          <InfoOutlined />
          This chart is automatically created with Stripe data coming from your
          connected{' '}
          <StyledAnchor onClick={routeToCampaigns}>
            Resilia Campaigns
          </StyledAnchor>
        </CalculationType>
      )}
      {!hasSeriesData && !isCampaignsLoading && (
        <DisconnectedCampaign>
          <ErrorOutlineOutlinedIcon
            htmlColor={palette.error.dark}
            sx={iconStyles}
          />
          <Box>
            <DisconnectedCampaignHeader>
              This goal was disconnected from a Campaign
            </DisconnectedCampaignHeader>
            <DisconnectedCampaignBody>
              Go to Settings in your Campaign, toggle on the option to
              automatically track your progress in the Manage Tab and select
              your Goal, once you save your campaign as public your chart will
              start to show your collected donations from Stripe.
            </DisconnectedCampaignBody>
          </Box>
        </DisconnectedCampaign>
      )}
      {isEditingChart && (
        <EditChartModal
          goal={goal}
          isOpen={isEditingChart}
          onClose={() => setIsEditingChart(false)}
          onEditChart={handleEditChart}
        />
      )}
    </ProgressChartContainer>
  )
}
